"use client";

import type { EhrWriteResponseBody } from "@frontend/lib/actions/actions";
import { usePersistentStore } from "@frontend/lib/stores/globalStore";
import {
  AlertCircle,
  CalendarOff,
  CalendarPlus,
  Check,
  ChevronRight,
  CircleCheck,
  ClipboardPlus,
  Copy,
  MailCheck,
  MailX,
  OctagonAlert,
  Pencil,
  PencilOff,
  UserX,
  Wrench,
} from "lucide-react";
import { type JSX, useState } from "react";
import { toast } from "sonner";
import type { PatientUpdateFailures } from "./PatientHeader/lib/PatientHeaderHelper";

// Reusable toast configuration objects
const BASE_TOAST_STYLE = {
  border: "1px solid #E1E1E1",
  color: "black",
  marginBottom: "50px",
};

const BASE_TOAST_CLASSNAMES = {
  toast: "rounded-xl",
  closeButton:
    "scale-[1.4] left-auto right-0 mt-4 mr-3 !border-none !bg-transparent !text-black",
};

const SUCCESS_TOAST_CONFIG = {
  duration: 8000,
  dismissible: true,
  closeButton: true,
  style: {
    ...BASE_TOAST_STYLE,
    background: "#DBF8EF",
  },
  classNames: BASE_TOAST_CLASSNAMES,
};

const ERROR_TOAST_CONFIG = {
  duration: Number.POSITIVE_INFINITY,
  closeButton: true,
  style: {
    ...BASE_TOAST_STYLE,
    background: "#FFF4F6",
  },
  classNames: BASE_TOAST_CLASSNAMES,
};

const INFO_TOAST_CONFIG = {
  duration: Number.POSITIVE_INFINITY,
  dismissible: true,
  closeButton: true,
  style: {
    ...BASE_TOAST_STYLE,
    background: "#FFF9E6",
  },
  classNames: BASE_TOAST_CLASSNAMES,
};

const MESSAGE_TOAST_CONFIG = {
  duration: 8000,
  closeButton: true,
  style: {
    ...BASE_TOAST_STYLE,
    background: "#F4F6FF",
  },
  classNames: BASE_TOAST_CLASSNAMES,
};

// Helper function to merge configurations with custom overrides
const mergeToastConfig = (
  baseConfig: Record<string, unknown>,
  overrides: Record<string, unknown> = {},
) => ({
  ...baseConfig,
  ...overrides,
  style: {
    ...(baseConfig.style as Record<string, unknown>),
    ...(overrides.style as Record<string, unknown>),
  },
  classNames: {
    ...(baseConfig.classNames as Record<string, unknown>),
    ...(overrides.classNames as Record<string, unknown>),
  },
});

export const triggerIntakeUpdateToast = (numFormsAdded: number) => {
  toast.success(
    <SuccessToast
      icon={<ClipboardPlus size={18} className="mb-[2px]" color="#249E00" />}
      message=""
      title="Forms updated successfully."
    />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const triggerResendReceiptSuccessToast = (message: string) => {
  toast.success(
    <SuccessToast title="Receipt sent successfully." message={message} />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const triggerGenerateReceiptFailureToast = (error: string) => {
  toast.error(
    <ErrorToast title="Error generating receipt." message={error} />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerResendReceiptFailureToast = (error: string) => {
  toast.error(
    <ErrorToast title="Error resending receipt." message={error} />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerIntakeResubmitFailureToast = (error: string) => {
  toast.error(
    <ErrorToast title="Error resubmitting intake pdf." message={error} />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerIntakeResubmitSuccessToast = (message: string) => {
  toast.success(
    <SuccessToast
      title="Intake pdf resubmitted successfully."
      message={message}
    />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const FormErrorToast = ({ errors }: { errors?: string[] }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div>
      <div className="flex gap-2 items-center mb-1">
        <OctagonAlert size={18} className="mb-[2px]" color="#FF416E" />
        <p className="font-semibold text-lg">Error</p>
      </div>
      <div className="flex flex-col gap-2">
        <p>
          Please fix the error(s) in the patient's profile before submitting.
        </p>

        {errors && errors.length > 0 && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronRight
                size={16}
                className={`transform transition-transform ${showDetails ? "rotate-90" : ""}`}
              />
              {showDetails ? "Hide" : "Show"} error details
            </button>

            {showDetails && (
              <ul className="mt-2 ml-4 text-sm">
                {errors.map((error, index) => (
                  <li
                    key={`error-${error}`}
                    className="list-disc font-mono text-sm ml-2 text-pcv2-text-grey"
                  >
                    {error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const triggerFormErrorToast = (errors?: string[]) => {
  toast.error(<FormErrorToast errors={errors} />, ERROR_TOAST_CONFIG);
};

export const triggerPatientCreationSuccessToast = () => {
  toast.success(
    <SuccessToast message={""} title="Patient created successfully!" />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const triggerPatientUpdateSuccessToast = () => {
  toast.success(
    <SuccessToast message={""} title={"Patient updated successfully!"} />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const triggerPatientUpdateFailureToast = () => {
  toast.error(
    <ErrorToast
      title="An error occurred."
      message={
        "The patient's information could not be updated. If this error persists, please contact support@superscript.nyc."
      }
    />,
    mergeToastConfig(ERROR_TOAST_CONFIG, { dismissible: true }),
  );
};
const PatientUpdateEHRFailureToastContent = ({
  failures,
  dirtyFields,
}: { failures: PatientUpdateFailures; dirtyFields: string[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const fieldToStrings = {
    dob: {
      name: "DOB",
      failureType: "redox",
    },
    email: {
      name: "email",
      failureType: "user",
    },
    primaryLanguage: {
      name: "language",
      failureType: "user",
    },
    consumerFirstName: {
      name: "first name",
      failureType: "redox",
    },
    consumerLastName: {
      name: "last name",
      failureType: "redox",
    },
    preferredName: {
      name: "preferred name",
      failureType: "athena",
    },
    primaryInsurance: {
      name: "primary insurance",
      failureType: "insurance",
    },
    secondaryInsurance: {
      name: "secondary insurance",
      failureType: "insurance",
    },
    tertiaryInsurance: {
      name: "tertiary insurance",
      failureType: "insurance",
    },
    sex: {
      name: "sex",
      failureType: "redox",
    },
    maritalStatus: {
      name: "marital status",
      failureType: "redox",
    },
    race: {
      name: "race",
      failureType: "redox",
    },
    ethnicity: {
      name: "ethnicity",
      failureType: "redox",
    },
    pharmacy: {
      name: "pharmacy",
      failureType: "pharmacy",
    },
    pcp: {
      name: "PCP",
      failureType: "pcp",
    },
    emergencyContact: {
      name: "emergency contact",
      failureType: "athena",
    },
    referralSource: {
      name: "referral source",
      failureType: "athena",
    },
    address: {
      name: "address",
      failureType: "redox",
    },
    phone: {
      name: "phone number",
      failureType: "user",
    },
  };

  const getFieldMapping = (field: string): string => {
    if (field.startsWith("emergencyContact")) return "emergencyContact";
    if (field.startsWith("pcp")) return "pcp";
    if (field.startsWith("referralSource")) return "referralSource";
    if (field.startsWith("pharmacy")) return "pharmacy";
    if (field.startsWith("preferred")) return "preferredName";
    if (["streetAddress", "city", "state", "zip", "country"].includes(field))
      return "address";
    return field;
  };

  const actualErrorFields = dirtyFields.filter((field) => {
    const fieldToCheck = getFieldMapping(field);
    const failureType = fieldToStrings[fieldToCheck]?.failureType;
    return failureType && failures[failureType];
  });

  const dedupedErrorFields = [
    ...new Set(actualErrorFields.map(getFieldMapping)),
  ];

  const multipleErrorMessage = (
    <p>
      There was an error writing some of the patient's information to Athena.
      Please finish updating the patient information in the EHR.
    </p>
  );

  const singleErrorMessage = fieldToStrings[dedupedErrorFields[0]]?.name ? (
    <p>
      There was an error writing the patient's{" "}
      <span className="font-semibold">
        {fieldToStrings[dedupedErrorFields[0]].name}
      </span>{" "}
      to Athena. Please finish updating the patient information in the EHR.
    </p>
  ) : (
    multipleErrorMessage
  );

  return (
    <div>
      <div className="flex gap-2 items-center mb-1">
        <UserX size={18} className="mb-[2px]" color="#FF416E" />
        <p className="font-semibold text-lg">Error</p>
      </div>
      <div key={"error-failures"} className="flex flex-col gap-2">
        {dedupedErrorFields.length === 1
          ? singleErrorMessage
          : multipleErrorMessage}

        {dedupedErrorFields.length > 1 && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronRight
                size={16}
                className={`transform transition-transform ${isExpanded ? "rotate-90" : ""}`}
              />
              {isExpanded ? "Hide" : "Show"} all errors
            </button>

            {isExpanded && (
              <ul className="mt-2 ml-4 text-sm">
                {dedupedErrorFields.map((field, index) => (
                  <li key={`error-${field}`} className="list-disc">
                    {fieldToStrings[field].name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const DebugToastTriggerButton = ({
  message,
  title = "Debug Info",
  className = "",
  label,
}: {
  message: string;
  title?: string;
  className?: string;
  label?: string;
}) => {
  const enabledDeveloperSettings =
    usePersistentStore.use.enabledDeveloperSettings();

  const showDebugWrenches = enabledDeveloperSettings.showDebugWrenches;

  if (!showDebugWrenches) return null;

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        triggerDebuggingToast(title, message);
      }}
      className={`rounded-full hover:text-yellow-400 transition-colors h-min hidden ${className} group-hover:flex items-center gap-1`}
      title={`DEBUG: show ${title}`}
      aria-label={`DEBUG: show ${title}`}
      type="button"
    >
      <Wrench size={18} />
      {label && <p>{label}</p>}
    </button>
  );
};

export const triggerDebuggingToast = (title: string, message: string) => {
  const DebuggingToast = () => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
      navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div>
        <div className="flex gap-2 items-center mb-1">
          <Wrench size={18} className="mb-[2px]" color="#5A5A5A" />
          <p className="font-semibold text-lg">{title}</p>
        </div>
        <div className="flex gap-2">
          <p className="text-sm break-words">{message}</p>
          <button
            type="button"
            onClick={copyToClipboard}
            className="self-start mt-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1 transition-colors"
          >
            {copied ? (
              <>
                <Check size={12} />
              </>
            ) : (
              <>
                <Copy size={12} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  toast.info(<DebuggingToast />, INFO_TOAST_CONFIG);
};

export const triggerUpdateEhrNoteSuccessToast = () => {
  toast.success(
    <SuccessToast
      icon={<Pencil size={18} className="mb-[2px]" color="#249E00" />}
      message={""}
      title="Appointment note updated."
    />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const triggerUpdateEhrNoteFailureToast = () => {
  toast.error(
    <ErrorToast
      icon={<PencilOff size={18} className="mb-[2px]" color="#FF416E" />}
      message={
        "The appointment note could not be updated. If this error persists, please contact support@superscript.nyc."
      }
      title="Error"
    />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerPatientUpdateEHRFailureToast = (
  failures: PatientUpdateFailures,
  dirtyFields: string[],
) => {
  toast.error(
    <PatientUpdateEHRFailureToastContent
      failures={failures}
      dirtyFields={dirtyFields}
    />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerPatientCreationFailureToast = (
  failureType: "consumer" | "user" | "insurance",
) => {
  const toastBody = (
    <div>
      <div className="flex gap-2 items-center mb-1">
        <OctagonAlert size={18} className="mb-[2px]" color="#FF416E" />
        <p className="font-semibold text-lg">Error</p>
      </div>
      <div key={`error-${failureType}`} className="flex gap-2 items-center">
        <p className="">
          {failureType === "consumer"
            ? "There was an error in creating this patient's account. Please contact Superscript support to resolve the issue."
            : failureType === "user"
              ? "There was an error in creating this patient's account. Please contact Superscript support to resolve the issue."
              : "There was an error in creating this patient's insurance(s). Please finish adding the insurance information on the patient's profile."}
        </p>
      </div>
    </div>
  );
  toast.error(toastBody, ERROR_TOAST_CONFIG);
};
export const triggerEHRWriteFailureToast = async (
  responseBody: EhrWriteResponseBody,
) => {
  const toastBody = (
    <div>
      <div className="flex gap-2 items-center mb-1">
        <OctagonAlert size={18} className="mb-[2px]" color="#FF416E" />
        <p className="font-semibold text-lg">Error</p>
      </div>
      {responseBody.ehrWritesFeedback &&
      responseBody.ehrWritesFeedback.length > 0 ? (
        responseBody.ehrWritesFeedback.map((feedback, index: number) => (
          <div
            key={`error-${feedback.operation}-${index}`}
            className="flex gap-2 items-center"
          >
            -
            <p className="">
              {feedback.message && feedback.message.length > 0
                ? feedback.message
                : "An error occurred in the last EHR write operation."}
            </p>
          </div>
        ))
      ) : (
        <div key={"error-default"} className="flex gap-2 items-center">
          <AlertCircle size={14} />
          <p className="">An error occurred in the last EHR write operation.</p>
        </div>
      )}
      <p className="">Please resolve the error(s) in Athena.</p>
    </div>
  );

  toast.error(toastBody, ERROR_TOAST_CONFIG);
};

export const triggerEHRCheckinSuccessToast = (
  firstname: string,
  lastname: string,
) => {
  toast.success(
    <SuccessToast
      message={`${firstname} ${lastname}'s appointment has been successfully marked as ready for visit.`}
    />,
    SUCCESS_TOAST_CONFIG,
  );
};

export const triggerPaymentFailureToast = (
  title = "Payment failed",
  message = "Please try again.",
) => {
  toast.error(
    <ErrorToast message={message} title={title} />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerPaymentSuccessToast = () => {
  toast.success(
    <SuccessToast message={""} title="Payment successful!" />,
    SUCCESS_TOAST_CONFIG,
  );
};

// not being called anymore in favor of modal
export const triggerEHRCheckinFailureToast = (
  firstname: string,
  lastname: string,
  description?: string,
) => {
  const message = `${firstname} ${lastname}'s appointment has not been marked as ready in Athena.`;
  toast.error(
    <ErrorToast message={message} />,
    mergeToastConfig(ERROR_TOAST_CONFIG, {
      dismissible: true,
      description: description,
      classNames: {
        closeButton:
          "scale-[1.4] left-auto right-0 mt-4 mr-3 !border-none !bg-transparent !text-black font-semibold",
      },
    }),
  );
};

export const triggerAppointmentArrivalFailureToast = (
  firstname: string,
  lastname: string,
) => {
  const message = `${firstname} ${lastname} could not be marked as arrived.`;
  const description =
    "Please resolve the error in Athena. If this error persists, please contact support@superscript.nyc.";
  toast.error(
    <ErrorToast message={description} title={message} />,
    mergeToastConfig(ERROR_TOAST_CONFIG, {
      dismissible: true,
      description: description,
      style: {
        marginRight: "20px",
      },
      classNames: {
        closeButton:
          "scale-[1.4] left-auto right-0 mt-4 mr-3 !border-none !bg-transparent !text-black font-semibold",
      },
    }),
  );
};

export const triggerAppointmentUnarrivalFailureToast = (
  firstname: string,
  lastname: string,
) => {
  const message = `Failed to unmark ${firstname} ${lastname} as arrived.`;
  const description =
    "Please resolve the error in Athena. If this error persists, please contact support@superscript.nyc.";
  toast.error(
    <ErrorToast message={description} title={message} />,
    mergeToastConfig(ERROR_TOAST_CONFIG, {
      dismissible: true,
      description: description,
      style: {
        marginRight: "20px",
      },
      classNames: {
        closeButton:
          "scale-[1.4] left-auto right-0 mt-4 mr-3 !border-none !bg-transparent !text-black font-semibold",
      },
    }),
  );
};

export const triggerUserMessageToast = (
  firstname: string,
  lastname: string,
  message: string,
  expandable = true,
) => {
  toast.success(
    expandable ? (
      <ExpandableMessageToast
        firstname={firstname}
        lastname={lastname}
        message={message}
      />
    ) : (
      <MessageToast
        firstname={firstname}
        lastname={lastname}
        message={message}
      />
    ),
    MESSAGE_TOAST_CONFIG,
  );
};

export const triggerUserMessageErrorToast = (
  firstname: string,
  lastname: string,
  message: string,
  customTitle?: string,
) => {
  toast.error(
    <ErrorToast
      message={message}
      title={
        customTitle
          ? customTitle
          : `Message not sent to ${firstname} ${lastname}`
      }
    />,
    ERROR_TOAST_CONFIG,
  );
};

export const triggerAppointmentToast = (
  action: "edited" | "created" | "cancelled",
) => {
  toast.success(
    <AppointmentToast action={action} />,
    mergeToastConfig(SUCCESS_TOAST_CONFIG, {
      style: {
        fontSize: "14px",
        fontWeight: "normal",
      },
    }),
  );
};

export const ErrorToast = ({
  message,
  title = "Error",
  icon,
}: { message: string; title?: string; icon?: JSX.Element }) => {
  const isMessageToast = title.toLowerCase().includes("message");
  if (!icon) {
    icon = isMessageToast ? (
      <MailX size={18} className="mb-[2px]" color="#FF416E" />
    ) : (
      <OctagonAlert
        size={18}
        className="mb-[2px] min-w-[18px]"
        color="#FF416E"
      />
    );
  }
  return (
    <div className="w-full min-w-fit">
      <div className="flex gap-2 items-center mb-1 w-full">
        {icon}
        <p className="font-semibold text-lg whitespace-nowrap">{title}</p>
      </div>
      <p className="">{message}</p>
    </div>
  );
};

export const MessageToast = ({ firstname, lastname, message }) => {
  return (
    <div>
      <div className="flex gap-2 items-center mb-1">
        <MailCheck size={18} className="mb-[2px]" color="#00A3FF" />
        <p className="font-semibold text-lg">
          Message sent to {firstname} {lastname}
        </p>
      </div>
      <p className="text-sm line-clamp-2 text-ellipsis">{message}</p>
    </div>
  );
};

export const SuccessToast = ({
  message,
  title = "Success!",
  icon,
}: { message: string; title?: string; icon?: JSX.Element }) => {
  if (!icon) {
    icon = title.toLowerCase().includes("cancelled") ? (
      <CalendarOff size={18} className="mb-[2px]" color="#249E00" />
    ) : (
      <CircleCheck size={18} className="mb-[2px]" color="#249E00" />
    );
  }
  return (
    <div>
      <div className="flex gap-2 items-center mb-1">
        {icon}
        <p className="font-semibold text-lg">{title}</p>
      </div>
      <p>{message}</p>
    </div>
  );
};

export const AppointmentToast = ({
  action,
}: { action: "edited" | "created" | "cancelled" }) => {
  const icon = (action) => {
    if (action === "edited") {
      return <Pencil size={18} className="mb-[2px]" color="#249E00" />;
    }
    if (action === "created") {
      return <CalendarPlus size={18} className="mb-[2px]" color="#249E00" />;
    }
    if (action === "cancelled") {
      return <CalendarOff size={18} className="mb-[2px]" color="#249E00" />;
    }
  };
  return (
    <div>
      <div className="flex gap-2 items-center mb-1">
        {icon(action)}
        <p className="font-semibold text-md">
          Appointment {action} successfully.
        </p>
      </div>
    </div>
  );
};

export const ExpandableMessageToast = ({ firstname, lastname, message }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <div className="flex gap-2 items-center mb-1">
        <MailCheck size={18} className="mb-[2px]" color="#00A3FF" />
        <p className="font-semibold text-lg">
          Message sent to {firstname} {lastname}
        </p>
      </div>
      <p className={`text-sm ${!isExpanded ? "line-clamp-2" : ""}`}>
        {message}
      </p>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-xs text-gray-500 hover:text-superscriptBlue mt-1"
      >
        {isExpanded ? "Collapse" : "Show all"}
      </button>
    </div>
  );
};

// need to add redirect action button to toast
